/**
 * Centralised icon maps and `EnhancedSelectOption` builders shared across
 * the typed encounter forms. Pulled out of the per-form modules so the
 * picker UI stays consistent without seven copies of the same dictionary.
 *
 * @agentic connections
 */

import {
  IconArrowDown,
  IconArrowUp,
  IconCheck,
  IconCurrencyDollar,
  IconHeartHandshake,
  IconMail,
  IconMessage,
  IconPhone,
  IconShield,
  IconSocial,
  IconUser,
  IconUsers,
  IconVideo,
  IconX,
} from "@tabler/icons-react";
import type { ReactNode } from "react";

import {
  ALL_ENCOUNTER_TYPE_SELECT_OPTIONS,
  ENCOUNTER_BILLABLE_CARD_OPTIONS,
  ENCOUNTER_DIRECTION_CARD_OPTIONS,
  ENCOUNTER_METHOD_CARD_OPTIONS,
  ENCOUNTER_TYPE_SELECT_OPTIONS,
} from "@benchmark/core/constants";
import type { EnhancedSelectOption } from "@/types/ui/component-props";

/**
 * Icon for each encounter method (phone / text / email / EMR / video / in-person).
 *
 * @agentic connections
 */
const ENCOUNTER_METHOD_ICON: Record<string, ReactNode> = {
  phone: <IconPhone size={20} />,
  text: <IconMessage size={20} />,
  email: <IconMail size={20} />,
  "emr-secure-message": <IconShield size={20} />,
  video: <IconVideo size={20} />,
  "in-person": <IconUsers size={20} />,
};

/**
 * Icon for each encounter direction (outbound / inbound).
 *
 * @agentic connections
 */
const ENCOUNTER_DIRECTION_ICON: Record<string, ReactNode> = {
  outbound: <IconArrowUp size={20} />,
  inbound: <IconArrowDown size={20} />,
};

/**
 * Icon for each billable status (billable / non-billable). Legacy + intake
 * forms render the billable selector with these icons.
 *
 * @agentic connections
 */
const ENCOUNTER_BILLABLE_ICON: Record<string, ReactNode> = {
  billable: <IconCheck size={20} />,
  "non-billable": <IconX size={20} />,
};

/**
 * Icon for each encounter type code. Combines v2 codes (the typed forms'
 * focus) with legacy codes for the legacy read-only form.
 *
 * @agentic connections
 */
const ENCOUNTER_TYPE_ICON: Record<string, ReactNode> = {
  // v2 codes
  "general-interaction": <IconUser size={20} />,
  "case-management-interaction": <IconSocial size={20} />,
  "clinical-intake-interaction": <IconHeartHandshake size={20} />,
  "clinical-follow-up-interaction": <IconHeartHandshake size={20} />,
  "cocm-intake-interaction": <IconMessage size={20} />,
  "cocm-follow-up-interaction": <IconMessage size={20} />,
  "unbilled-intake-interaction": <IconHeartHandshake size={20} />,
  "unbilled-follow-up-interaction": <IconHeartHandshake size={20} />,
  // legacy v1 codes (read-only)
  outreach: <IconMessage size={20} />,
  initial: <IconHeartHandshake size={20} />,
  "check-in-evaluation": <IconUser size={20} />,
  "follow-up": <IconHeartHandshake size={20} />,
};

/**
 * Builds the method-card options with icons. Optional `filter` lets intake
 * forms restrict to phone+video.
 *
 * @agentic connections
 */
export const buildMethodOptions = (
  filter?: (code: string) => boolean,
): EnhancedSelectOption[] => {
  const source = filter
    ? ENCOUNTER_METHOD_CARD_OPTIONS.filter((opt) => filter(opt.value))
    : ENCOUNTER_METHOD_CARD_OPTIONS;
  return source.map((opt) => ({
    ...opt,
    icon: ENCOUNTER_METHOD_ICON[opt.value] ?? <IconPhone size={20} />,
  }));
};

/**
 * Builds the direction-card options with icons.
 *
 * @agentic connections
 */
export const buildDirectionOptions = (): EnhancedSelectOption[] =>
  ENCOUNTER_DIRECTION_CARD_OPTIONS.map((opt) => ({
    ...opt,
    icon: ENCOUNTER_DIRECTION_ICON[opt.value] ?? <IconArrowUp size={20} />,
  }));

/**
 * Builds the billable-card options. Legacy form opts in via the icon set;
 * intake forms render this without per-option icons (the card content
 * conveys the meaning).
 *
 * @agentic connections
 */
export const buildBillableOptions = (
  withIcons = false,
): EnhancedSelectOption[] =>
  ENCOUNTER_BILLABLE_CARD_OPTIONS.map((opt) => ({
    ...opt,
    ...(withIcons
      ? {
          icon: ENCOUNTER_BILLABLE_ICON[opt.value] ?? (
            <IconCurrencyDollar size={20} />
          ),
        }
      : {}),
  }));

/**
 * Builds the v2 encounter-type options with icons. Used by typed forms
 * when their Type step is shown (free creation only).
 *
 * @agentic connections
 */
export const buildCurrentTypeOptions = (): EnhancedSelectOption[] =>
  ENCOUNTER_TYPE_SELECT_OPTIONS.map((opt) => ({
    ...opt,
    icon: ENCOUNTER_TYPE_ICON[opt.value] ?? <IconMessage size={20} />,
  }));

/**
 * Builds the all-types option list (v1 + v2) for the legacy form's
 * read-only Type step. Legacy options carry a "(Legacy)" suffix.
 *
 * @agentic connections
 */
export const buildAllTypeOptions = (): EnhancedSelectOption[] =>
  ALL_ENCOUNTER_TYPE_SELECT_OPTIONS.map((opt) => ({
    ...opt,
    label: opt.deprecated ? `${opt.label} (Legacy)` : opt.label,
    icon: ENCOUNTER_TYPE_ICON[opt.value] ?? <IconMessage size={20} />,
  }));
